
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { SparklesIcon, CheckCircleIcon, PlayIcon, ShoppingBagIcon } from '../components/icons';
import { CDN_URLS, OCI_FOLDERS } from '../config';

interface DigitalBoxRequestPageProps {
    onNavigate: (page: string) => void;
}

const DigitalBoxRequestPage: React.FC<DigitalBoxRequestPageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState<'request' | 'processing' | 'result'>('request');
    const [preferences, setPreferences] = useState({
        occasion: '',
        vibe: '',
        budget: ''
    });

    const handleGenerate = () => {
        setStep('processing');
        // Mock API call
        setTimeout(() => {
            setStep('result');
        }, 2500);
    };

    // Mock Result Data
    const resultItems = [
        { id: 1, name: 'Structured Blazer', price: '$120', img: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Business/business_1.png` },
        { id: 2, name: 'Silk Blouse', price: '$85', img: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_1.png` },
        { id: 3, name: 'Tailored Trousers', price: '$95', img: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Business/business_2.png` },
        { id: 4, name: 'Leather Loafers', price: '$110', img: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Accessories/shoes_1.png` }, // Fallback logic needed if image missing
        { id: 5, name: 'Minimalist Totebag', price: '$150', img: `${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Accessories/bag_1.png` },
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#18181B] py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">

                <AnimatePresence mode="wait">
                    {step === 'request' && (
                        <motion.div
                            key="request"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-xl p-8 sm:p-12 border border-gray-100 dark:border-[#C0A172]/20"
                        >
                            <div className="text-center mb-10">
                                <span className="inline-block p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 mb-4">
                                    <ShoppingBagIcon className="w-8 h-8" />
                                </span>
                                <h1 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-4">
                                    {t('digitalBox_title') || "Curate My Digital Box"}
                                </h1>
                                <p className="text-gray-600 dark:text-gray-300 max-w-lg mx-auto">
                                    {t('digitalBox_subtitle') || "Tell us what you need, and our AI will handpick 5 cohesive items for you, presented in a personalized video lookbook."}
                                </p>
                            </div>

                            <div className="space-y-6 max-w-md mx-auto">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('digitalBox_label_occasion') || "What's the occasion?"}
                                    </label>
                                    <select
                                        className="w-full rounded-xl border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-3 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                        onChange={(e) => setPreferences({ ...preferences, occasion: e.target.value })}
                                    >
                                        <option value="">Select an occasion...</option>
                                        <option value="work">Work / Office</option>
                                        <option value="date">Date Night</option>
                                        <option value="wedding_guest">Wedding Guest</option>
                                        <option value="casual">Weekend Casual</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('digitalBox_label_vibe') || "What's the vibe?"}
                                    </label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {['Minimalist', 'Boho', 'Edgy', 'Classic'].map(vibe => (
                                            <button
                                                key={vibe}
                                                onClick={() => setPreferences({ ...preferences, vibe })}
                                                className={`py-3 px-4 rounded-xl text-sm font-medium border transition-all ${preferences.vibe === vibe
                                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-400'
                                                        : 'border-gray-200 text-gray-600 hover:border-indigo-300 dark:border-gray-600 dark:text-gray-300'
                                                    }`}
                                            >
                                                {vibe}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-6">
                                    <button
                                        onClick={handleGenerate}
                                        disabled={!preferences.occasion || !preferences.vibe}
                                        className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-full shadow-lg text-lg font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105"
                                    >
                                        <SparklesIcon className="w-5 h-5 mr-2" />
                                        {t('digitalBox_cta_generate') || "Generate My Box"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 'processing' && (
                        <motion.div
                            key="processing"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center py-20 text-center"
                        >
                            <div className="relative w-24 h-24 mb-8">
                                <div className="absolute inset-0 rounded-full border-4 border-indigo-100 dark:border-indigo-900/30"></div>
                                <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
                                <SparklesIcon className="absolute inset-0 m-auto w-8 h-8 text-indigo-600 animate-pulse" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                {t('digitalBox_processing_title') || "Curating your look..."}
                            </h2>
                            <p className="text-gray-500 dark:text-gray-400">
                                {t('digitalBox_processing_desc') || "Our AI is analyzing thousands of items to match your vibe."}
                            </p>
                        </motion.div>
                    )}

                    {step === 'result' && (
                        <motion.div
                            key="result"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-2xl overflow-hidden border border-gray-100 dark:border-[#C0A172]/20"
                        >
                            {/* Fake Video Player Header */}
                            <div className="relative h-64 sm:h-80 bg-black group cursor-pointer overflow-hidden">
                                <img src={`${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_1.png`} alt="Lookbook Cover" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/50 group-hover:scale-110 transition-transform">
                                        <PlayIcon className="w-8 h-8 text-white ml-1" />
                                    </div>
                                </div>
                                <div className="absolute bottom-6 left-6 text-white">
                                    <span className="bg-indigo-600 text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider mb-2 inline-block">Your Lookbook</span>
                                    <h2 className="text-2xl font-serif font-bold">The {preferences.vibe} {preferences.occasion} Edit</h2>
                                </div>
                            </div>

                            <div className="p-8">
                                <div className="flex justify-between items-center mb-8">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">In Your Box</h3>
                                    <span className="text-sm text-gray-500">Total Value: $560</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">
                                    {resultItems.map((item, idx) => (
                                        <motion.div
                                            key={item.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.1 }}
                                            className="group"
                                        >
                                            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-gray-100 mb-3 relative">
                                                <img src={item.img} alt={item.name} className="w-full h-full object-cover" onError={(e) => {
                                                    // Fallback if image fails
                                                    (e.target as HTMLImageElement).src = 'https://placehold.co/300x400?text=Item';
                                                }} />
                                                <button className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity text-indigo-600 hover:bg-indigo-50" title="Shop Now">
                                                    <ShoppingBagIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <h4 className="font-medium text-gray-900 dark:text-white text-sm">{item.name}</h4>
                                            <p className="text-gray-500 text-xs">{item.price}</p>
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="mt-12 pt-8 border-t border-gray-100 dark:border-gray-800 flex justify-center gap-4">
                                    <button className="px-8 py-3 rounded-full font-bold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors">
                                        Regenerate
                                    </button>
                                    <button className="px-8 py-3 rounded-full font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-indigo-500/30 transition-all">
                                        Shop All Items
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default DigitalBoxRequestPage;
